import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { rrcConnectionCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the RrcConnectionEstablishmentCell component
 */
interface RrcConnectionEstablishmentCellProps {
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
}

/**
 * RRC Connection Establishment Success Rate Chart (LTE Cell Level)
 * 
 * Displays Radio Resource Control (RRC) connection establishment metrics for individual LTE cells:
 * - RRC Connection Establishment Success (count)
 * - RRC Connection Establishment Attempt (count)
 * - RRC Connection Establishment Success Rate (percentage)
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level RRC connection metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <RrcConnectionEstablishmentCell
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 *   cellName="173716_145"
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (rrcConnectionCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/rrc-connection-establishment?cell_name={cellName}
 */

/**
 * RRC Connection Establishment Cell-Level Chart Component
 * 
 * Displays RRC (Radio Resource Control) connection establishment metrics for a specific cell:
 * - RRC Connection Success (count)
 * - RRC Connection Attempt (count)
 * - RRC Connection Success Rate (percentage)
 * 
 * Uses the SiteCharts template for consistent visualization.
 */
const RrcConnectionEstablishmentCell: React.FC<RrcConnectionEstablishmentCellProps> = ({ 
  onFullscreenToggle, 
  isFullscreen,
  cellName
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={rrcConnectionCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default RrcConnectionEstablishmentCell;
